import { useMemo } from 'react'
import type { ClaudeSession } from '@/features/events/conversation/state/types'
import type { SessionStatus } from '@/infra/sse/status'
import { SESSION_STATUS_TAB_MAPPING } from '@/features/events/shared/types'
import type { SessionStatusTab } from '@/features/events/shared/types'
import type { ParkedItems } from './useParkedItems'

export interface SessionStatusBuckets {
  readonly buckets: Readonly<Record<SessionStatusTab, readonly ClaudeSession[]>>
  readonly counts: Readonly<Record<SessionStatusTab, number>>
}

function resolveStatusTab(status: SessionStatus | null): Exclude<SessionStatusTab, 'parked'> {
  if (status === null) return 'active'
  for (const [tab, statuses] of Object.entries(SESSION_STATUS_TAB_MAPPING)) {
    if ((statuses as readonly string[]).includes(status))
      return tab as Exclude<SessionStatusTab, 'parked'>
  }
  return 'active'
}

function isSessionParked(session: ClaudeSession, parkedItems: ParkedItems): boolean {
  return (
    parkedItems.sessions.has(session.sessionId) ||
    parkedItems.projects.has(session.contextWorkDirectory) ||
    parkedItems.hosts.has(session.userHostDetails.hostName)
  )
}

export function useSessionStatusBuckets(
  sessions: readonly ClaudeSession[],
  getSessionStatus: (sessionId: string) => SessionStatus | null,
  parkedItems: ParkedItems,
): SessionStatusBuckets {
  return useMemo(() => {
    const active: ClaudeSession[] = []
    const resumable: ClaudeSession[] = []
    const parked: ClaudeSession[] = []

    for (const session of sessions) {
      const tab = resolveStatusTab(getSessionStatus(session.sessionId))

      if (tab === 'resumable') {
        resumable.push(session)
        continue
      }

      if (isSessionParked(session, parkedItems)) {
        parked.push(session)
        continue
      }

      if (tab === 'active') {
        active.push(session)
      }
    }

    return {
      buckets: { active, resumable, parked },
      counts: { active: active.length, resumable: resumable.length, parked: parked.length },
    }
  }, [sessions, getSessionStatus, parkedItems])
}
